import React from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatWholeUnitPrice } from '../../util/listingDisplay';
import { getListingCapabilities } from '../../util/dualListing';

import { NamedLink } from '../../components';

import { priceData } from './ListingPage.shared';
import css from './SectionListingHeader.module.css';

/**
 * Walks the selected category path down to the deepest chosen level, returning
 * each level's id and name so the trail can link back to a filtered search.
 */
export const getCategoryTrail = (publicData, categories) => {
  const trail = [];
  let options = categories || [];
  for (let level = 1; options.length > 0; level++) {
    const id = publicData?.[`categoryLevel${level}`];
    const match = id ? options.find(c => c.id === id) : null;
    if (!match) {
      break;
    }
    trail.push({ id: match.id, name: match.name, level });
    options = match.subcategories || [];
  }
  return trail;
};

/**
 * Home / Dresses / <this listing>. Rendered above the three columns rather than
 * inside one, so a long title has the whole page width to wrap into.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title listing title
 * @param {Object} props.publicData listing publicData
 * @param {Array} props.categories hosted category tree
 * @returns {JSX.Element}
 */
export const ListingBreadcrumbs = props => {
  const intl = useIntl();
  const { title, publicData, categories = [] } = props;
  const trail = getCategoryTrail(publicData, categories);

  return (
    <nav
      className={css.breadcrumbs}
      aria-label={intl.formatMessage({ id: 'ListingPage.breadcrumbsLabel' })}
    >
      <ol className={css.breadcrumbList}>
        <li className={css.breadcrumbItem}>
          <NamedLink name="LandingPage" className={css.breadcrumbLink}>
            <FormattedMessage id="ListingPage.breadcrumbHome" />
          </NamedLink>
        </li>
        {trail.map(crumb => (
          <li key={crumb.id} className={css.breadcrumbItem}>
            <span className={css.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <NamedLink
              name="SearchPage"
              to={{ search: `?pub_categoryLevel${crumb.level}=${crumb.id}` }}
              className={css.breadcrumbLink}
            >
              {crumb.name}
            </NamedLink>
          </li>
        ))}
        <li className={css.breadcrumbItem}>
          <span className={css.breadcrumbSeparator} aria-hidden="true">
            /
          </span>
          <span className={css.breadcrumbCurrent} aria-current="page">
            {title}
          </span>
        </li>
      </ol>
    </nav>
  );
};

/**
 * Listing page headline: top-level category as a quiet eyebrow, the item name as
 * the page's h1, and the retail value struck through beneath it.
 *
 * Designer and size are not repeated here — they are Console listing fields and
 * appear in the Details panel with the rest of them.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title listing title
 * @param {Object} props.publicData listing publicData
 * @param {Array} props.categories hosted category tree
 * @param {string} props.currency marketplace currency
 * @param {Object} [props.price] listing price (Money) — daily rental or purchase
 * @returns {JSX.Element}
 */
const SectionListingHeader = props => {
  const intl = useIntl();
  const { title, publicData, categories = [], currency, price } = props;

  const retail = formatWholeUnitPrice(publicData?.retail_value, currency, intl);
  const { buy, rent } = getListingCapabilities(publicData);
  const { formattedPrice } = priceData(price, currency, intl);
  // Sale listings show the purchase price next to retail. Rent listings keep
  // the rate in the order panel, so it is not repeated here.
  const actualPrice = buy && !rent ? formattedPrice : null;
  const eyebrow = getCategoryTrail(publicData, categories)[0]?.name;

  return (
    <header className={css.root}>
      {eyebrow ? <p className={css.eyebrow}>{eyebrow}</p> : null}
      <h1 className={css.title}>{title}</h1>
      {retail || actualPrice ? (
        <p className={css.retail}>
          {retail ? (
            <>
              <span className={css.retailLabel}>
                <FormattedMessage id="ListingPage.retailLabel" />
              </span>
              <span className={css.retailValue}>{retail}</span>
            </>
          ) : null}
          {actualPrice ? <span className={css.actualPrice}>{actualPrice}</span> : null}
        </p>
      ) : null}
    </header>
  );
};

export default SectionListingHeader;
